import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Pencil, Upload, RotateCcw, X, Loader2, Check } from "lucide-react";
import { adminApi, getToken, apiError } from "@/lib/adminApi";
import { setMediaOverride, slotDefault, slotMeta } from "@/data/pdpMedia";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const EDIT_KEY = "ag_edit_media";

// Push a URL onto every DOM element bound to this slot (instant visual swap).
function applyToDom(slot, url) {
  document.querySelectorAll(`[data-media-slot="${slot}"]`).forEach((el) => {
    if (el.tagName === "VIDEO") {
      el.setAttribute("src", url);
      el.load?.();
      el.play?.().catch(() => {});
    } else {
      el.setAttribute("src", url);
    }
  });
}

export function MediaEditor() {
  const loc = useLocation();
  const isAdmin = !!getToken();
  const hidden = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/checkout");

  const [on, setOn] = useState(() => localStorage.getItem(EDIT_KEY) === "1");
  const [targets, setTargets] = useState([]);
  const [busySlot, setBusySlot] = useState(null);
  const fileRef = useRef(null);
  const pending = useRef(null); // { slot, type }

  const toggle = () => {
    setOn((v) => {
      const next = !v;
      localStorage.setItem(EDIT_KEY, next ? "1" : "0");
      return next;
    });
  };

  const measure = useCallback(() => {
    const els = Array.from(document.querySelectorAll("[data-media-slot]"));
    const seen = new Set();
    const next = [];
    els.forEach((el) => {
      const slot = el.getAttribute("data-media-slot");
      if (!slot || seen.has(slot)) return; // one control per slot (first visible instance)
      const r = el.getBoundingClientRect();
      if (r.width < 24 || r.height < 24) return;
      if (r.bottom < 0 || r.top > window.innerHeight) return; // only on-screen
      seen.add(slot);
      next.push({
        slot,
        type: el.getAttribute("data-media-type") || "image",
        top: r.top, left: r.left, width: r.width, height: r.height,
      });
    });
    setTargets(next);
  }, []);

  useEffect(() => {
    if (!on || hidden || !isAdmin) { setTargets([]); return; }
    measure();
    const id = setInterval(measure, 350);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      clearInterval(id);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [on, hidden, isAdmin, measure, loc.pathname]);

  const openPicker = (slot, type) => {
    pending.current = { slot, type };
    if (fileRef.current) {
      fileRef.current.value = "";
      fileRef.current.accept = type === "video" ? "video/*" : "image/*";
      fileRef.current.click();
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    const p = pending.current;
    if (!file || !p) return;
    setBusySlot(p.slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post(`/admin/media/${p.slot}`, fd);
      const abs = data.url?.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      setMediaOverride(p.slot, abs);
      applyToDom(p.slot, abs);
      toast.success("Media updated", { description: slotMeta(p.slot)?.label || p.slot });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Upload failed. Please try again.");
    } finally {
      setBusySlot(null);
      pending.current = null;
    }
  };

  const onReset = async (slot) => {
    setBusySlot(slot);
    try {
      await adminApi.delete(`/admin/media/${slot}`);
      const def = slotDefault(slot);
      setMediaOverride(slot, "");
      const abs = def.startsWith("http") || def.startsWith("/") ? (def.startsWith("http") ? def : `${BACKEND}${def}`) : def;
      applyToDom(slot, abs || def);
      toast.success("Reset to original", { description: slotMeta(slot)?.label || slot });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not reset.");
    } finally {
      setBusySlot(null);
    }
  };

  if (hidden || !isAdmin) return null;

  return createPortal(
    <>
      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="media-editor-file" />

      {/* Floating toggle */}
      <button onClick={toggle} data-testid="media-editor-toggle"
        className={`fixed bottom-5 right-5 z-[9998] flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold shadow-[0_12px_30px_-10px_rgba(0,0,0,0.5)] transition-all hover:scale-105 ${on ? "bg-[#2C2B2C] text-white" : "bg-white text-[#2C2B2C] ring-1 ring-[#E5DBC8]"}`}>
        {on ? <><Check className="h-4 w-4 text-[#FBC4A8]" /> Editing media</> : <><Pencil className="h-4 w-4 text-[#E97C6A]" /> Edit media</>}
      </button>

      {on && (
        <>
          {/* Overlays per media slot */}
          {targets.map((t) => (
            <div key={t.slot} className="pointer-events-none fixed z-[9990]"
              style={{ top: t.top, left: t.left, width: t.width, height: t.height }}>
              <span className="absolute inset-0 rounded-lg ring-2 ring-[#E97C6A]/80 ring-offset-0" />
              <div className="pointer-events-auto absolute right-2 top-2 flex gap-1.5">
                <button onClick={() => openPicker(t.slot, t.type)} disabled={busySlot === t.slot}
                  data-testid={`media-edit-${t.slot}`}
                  className="flex items-center gap-1.5 rounded-full bg-[#2C2B2C] px-3 py-1.5 text-[0.7rem] font-semibold text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-60">
                  {busySlot === t.slot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 text-[#FBC4A8]" />} Replace
                </button>
                <button onClick={() => onReset(t.slot)} disabled={busySlot === t.slot}
                  data-testid={`media-reset-${t.slot}`} title="Reset to original"
                  className="flex items-center justify-center rounded-full bg-white/95 px-2.5 py-1.5 text-[#8A7F86] shadow-lg transition-colors hover:text-[#2C2B2C]">
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}

          {/* Helper banner */}
          <div data-testid="media-editor-banner"
            className="fixed bottom-5 left-5 z-[9998] flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs text-[#6E6168] shadow-[0_12px_30px_-12px_rgba(0,0,0,0.4)] ring-1 ring-[#E5DBC8]">
            <Pencil className="h-3.5 w-3.5 text-[#E97C6A]" />
            Click <b className="mx-0.5 text-[#2C2B2C]">Replace</b> on any photo or video to swap it live.
          </div>
        </>
      )}
    </>,
    document.body
  );
}
